import { identity, pickBy } from 'lodash-unified';
import { httpClientRepository } from '@bg-shared/infrastructure/HttpClient';
import { BetType } from '@bg-shared/enums/BetType';
import { API_URLS } from '@bg-shared/constants/apiUrls';
import { MAX_COMBO_ODDS_COUNT } from '@bg-shared/constants/constants';
import { ISingleBet } from '@bg-shared/business/Reports/interfaces_legacy/Bets';
import { ILotteryItem } from '@bg-shared/interfaces/ILotteryItem';
import { analyticsApi } from '@bg-shared/business/Analytics';
import { partnerSettingsStore } from '@bg-shared/business/PartnerSettings';
import {
    IBatchResponse,
    IBatchResponseLegacy,
    IBetsByRun,
    ICombinationResponse,
    IFormattedResponse,
    IPlaceBetParams,
    ISingleResponse,
    ISubscriptionResponse,
} from './interfaces';
import { formatBetItems, getRunId, findBetItemIds } from './utils';

// TODO: migrate / remove interfaces_legacy

class BettingService {
    async placeBetSingle(params: IPlaceBetParams): Promise<IFormattedResponse> {
        const [odd] = params.odds;

        return httpClientRepository
            .post<ISingleResponse>(API_URLS.PAYIN, {
                data: pickBy(
                    {
                        type: BetType.SINGLE,
                        runId: getRunId(odd),
                        runRoundId: odd.draw.runRoundId,
                        items: formatBetItems(odd.items),
                        amount: params.amountValue,
                        oddId: odd.id,
                        oddValue: odd.value,
                        gameId: params.gameId,
                        cashOutMultiplier: params.cashOutMultiplier,
                    },
                    identity,
                ),
                timeout: partnerSettingsStore.requestTimeout,
            })
            .then((response) => {
                if (response) {
                    analyticsApi.trackPlaceBetSingle(response.bet.id, !!odd.random);
                }

                return {
                    betId: response.bet.id,
                };
            });
    }

    async placeBetSubscription(params: IPlaceBetParams): Promise<IFormattedResponse> {
        const [odd] = params.odds;

        return httpClientRepository
            .post<ISubscriptionResponse>(API_URLS.PAYIN, {
                data: pickBy(
                    {
                        type: BetType.SUBSCRIPTION,
                        runId: getRunId(odd),
                        oddId: odd.id,
                        oddValue: odd.value,
                        amount: params.amountValue,
                        items: formatBetItems(odd.items),
                        runsCount: params.runsCount,
                        gameId: params.gameId,
                    },
                    identity,
                ),
                timeout: partnerSettingsStore.requestTimeout,
            })
            .then((response) => {
                if (response) {
                    analyticsApi.trackPlaceBetSubscription(response.subscription.id, !!odd.random);
                }

                return {
                    betId: response.subscription.id,
                };
            });
    }

    async placeBatch(params: IPlaceBetParams): Promise<IFormattedResponse> {
        return httpClientRepository
            .post<IBatchResponseLegacy | IBatchResponse>(API_URLS.BATCH, {
                data: {
                    bets: params.odds.map((odd) =>
                        pickBy(
                            {
                                type: params.runsCount > 1 ? BetType.SUBSCRIPTION : BetType.SINGLE,
                                runId: getRunId(odd),
                                oddId: odd.id,
                                oddValue: odd.value,
                                amount: params.amountValue,
                                items: formatBetItems(odd.items),
                                runsCount: params.runsCount,
                                gameId: params.gameId,
                            },
                            identity,
                        ),
                    ),
                },
                timeout: partnerSettingsStore.requestTimeout,
            })
            .then((response) => {
                analyticsApi.trackPlaceBetMulti(response);

                return null;
            });
    }

    async placeBetCombo(params: IPlaceBetParams): Promise<IFormattedResponse> {
        return httpClientRepository
            .post<ICombinationResponse>(API_URLS.PAYIN, {
                data: pickBy(
                    {
                        type: BetType.COMBINATION,
                        oddValue: params.totalOddValue,
                        amount: params.amountValue,
                        odds: params.odds.map((odd) =>
                            pickBy(
                                {
                                    oddId: odd.id,
                                    oddValue: odd.value,
                                    runId: getRunId(odd),
                                    items: formatBetItems(odd.items),
                                    gameId: odd.gameId,
                                },
                                identity,
                            ),
                        ),
                    },
                    identity,
                ),
                timeout: partnerSettingsStore.requestTimeout,
            })
            .then((response) => {
                if (response) {
                    analyticsApi.trackPlaceBetCombo(
                        response.combination.id,
                        params.odds.some((odd) => odd.random),
                    );
                }

                return {
                    betId: response.combination.id,
                };
            });
    }

    public async placeBet(params: IPlaceBetParams): Promise<IFormattedResponse> {
        try {
            if (params.isBatch) {
                return await this.placeBatch(params);
            }

            if (params.odds.length === MAX_COMBO_ODDS_COUNT) {
                return await this.placeBetCombo(params);
            }

            if (params.runsCount > 1) {
                return await this.placeBetSubscription(params);
            }

            return await this.placeBetSingle(params);
        } catch (error) {
            analyticsApi.trackInvalidBets(error);
            throw error;
        }
    }

    getBetByRun(runId: number): Promise<IBetsByRun[]> {
        return httpClientRepository.get<IBetsByRun[]>(`${API_URLS.BET_BY_RUN}/${runId}`);
    }

    public repeatBets(
        repeatableBets: Array<{ bet: ISingleBet; gameItems: ILotteryItem[]; runRoundId?: number }>,
    ) {
        return httpClientRepository
            .post<IBatchResponseLegacy | IBatchResponse>(API_URLS.BATCH, {
                data: {
                    bets: repeatableBets.map((repeatableBet) => ({
                        amount: Number(repeatableBet.bet.amountBet),
                        oddId: repeatableBet.bet.oddsId,
                        oddValue: Number(repeatableBet.bet.oddsValue),
                        oddClass: repeatableBet.bet.oddsClass,
                        runId: repeatableBet.bet.runId,
                        type: BetType.SINGLE,
                        items: findBetItemIds(repeatableBet.bet, repeatableBet.gameItems),
                        runRoundId: repeatableBet.runRoundId || undefined,
                    })),
                },
            })
            .then((response) => {
                if (response) {
                    analyticsApi.trackPlaceBetMulti(response, true);
                }

                return response;
            })
            .catch((error) => {
                analyticsApi.trackInvalidBets(error);
                throw error;
            });
    }
}

export const bettingService = new BettingService();
